<template>
  <v-data-table-server
    v-model:page="page"
    :items-length="count"
    :items-per-page="25"
    :items-per-page-options="[{ value: 25, title: '25' }]"
    :hover="vehicleLogs.length > 0"
    item-value="name"
    :headers="vehicleDataTableHeaders"
    :items="vehicleLogs"
    :loading="loading"
    color="primary"
    sticky
    fixed-header
    density="default"
    height="calc(100% - 37px)"
    style="height: calc(100% - 93px); width: 100%"
    :row-props="generateRowProps"
  >
    <template #[`item.index`]="{ index }">
      {{ index + 1 }}
    </template>

    <template #[`item.actions`]="{ item }">
      <VehicleLogDetailOptionsButton
        :vehicleLog="item"
        @vehicle-log-edit-pressed="
          (vehicle: IVehicleLog) => emit('vehicle-log-edit-pressed', vehicle)
        "
        @vehicle-log-delete-pressed="
          (vehicle: IVehicleLog) => emit('vehicle-log-delete-pressed', vehicle)
        "
      />
    </template>

    <template v-slot:no-data>
      <NoDataAvailablePlaceholder
        header="No vehicles available"
        header-class="font-weight-medium text-h5"
      >
        <template #prepend>
          <v-card
            variant="flat"
            class="mask-bottom-card mb-3"
            color="transparent"
            width="180"
            height="120"
          >
            <v-list
              style="color: rgba(var(--v-theme-on-surface), var(--v-disabled-opacity))"
              variant="outlined"
              density="compact"
              lines="two"
              class="pt-0"
            >
              <v-list-item class="rounded-lg-xl mb-3 px-3">
                <template v-slot:prepend>
                  <v-avatar size="32" class="me-n1" color="green-darken-2 text-white">
                    JD
                  </v-avatar>
                </template>
                <v-skeleton-loader class="me-n3 ms-n4 my-n2" type="text, text" boilerplate />
              </v-list-item>
              <v-list-item class="rounded-lg-xl mb-3 px-3">
                <template v-slot:prepend>
                  <v-avatar size="32" class="me-n1" color="amber-darken-2 text-white">
                    DM
                  </v-avatar>
                </template>
                <v-skeleton-loader class="me-n3 ms-n4 my-n2" type="text, text" boilerplate />
              </v-list-item>
            </v-list>
          </v-card>
        </template>

        <template #description>
          <p class="mt-0 mb-6 text-caption text-medium-emphasis">
            <span>
              A <span class="text-primary">Vehicle Log</span> will be created from the Tag mobile
              app and can be managed here</span
            >
          </p>
        </template>
      </NoDataAvailablePlaceholder>
    </template>
  </v-data-table-server>
</template>

<script lang="ts" setup>
import { format } from 'date-fns'

import type { IVehicleLog } from '@/models/fleet-management'
import type { ReadonlyTableHeaders } from '@/utils/tables'

import NoDataAvailablePlaceholder from '@/components/common/NoDataAvailablePlaceholder.vue'
import VehicleLogDetailOptionsButton from '@/components/fleet-management/vehicles-logs/VehicleLogDetailOptionsButton.vue'

interface Props {
  vehicleLogs: IVehicleLog[]
  count: number
  loading: boolean
}

interface Emit {
  (e: 'vehicle-log-selected', vehicle: IVehicleLog): void
  (e: 'vehicle-log-edit-pressed', vehicle: IVehicleLog): void
  (e: 'vehicle-log-delete-pressed', vehicle: IVehicleLog): void
}

const page = defineModel<number>('page')

const emit = defineEmits<Emit>()
defineProps<Props>()

const vehicleDataTableHeaders: ReadonlyTableHeaders = [
  {
    title: '#',
    align: 'start',
    key: 'index',
    width: 20,
    sortable: false
  },
  {
    title: 'Plate',
    align: 'start',
    key: 'vehicle.plate',
    width: 100,
    sortable: false
  },

  {
    title: 'Year',
    align: 'start',
    key: 'vehicle.year',
    width: 100,
    sortable: false
  },

  {
    title: 'Vehicle',
    align: 'start',
    key: 'vehicle.model',
    sortable: false
  },
  {
    title: 'Guards',
    align: 'start',
    key: 'owner',
    sortable: false
  },
  {
    title: 'Mileage',
    align: 'end',
    key: 'mileage',
    sortable: false
  },
  {
    title: 'Fuel Percentage',
    align: 'end',
    key: 'fuel_percentage',
    value: (entry: any) => `${entry.fuel_percentage}%`,

    sortable: false
  },
  {
    title: 'Created at',
    align: 'start',
    key: 'created',
    value: (entry: any) => format(entry.created, 'MMMM d, yyyy h:mma'),
    width: 250,
    sortable: false
  },

  { title: '', key: 'actions', align: 'center', sortable: false }
]

function generateRowProps(props: { index: number; item: IVehicleLog }) {
  return {
    // apply click listener to row props to open detail drawer
    onClick: () => emit('vehicle-log-selected', props.item)
  }
}
</script>
